/**
 * @file ProductCard.tsx
 *
 * Grid-view card for a single product: image with a stock/draft badge, name,
 * and price (sale price with the struck-through regular price when on sale).
 * The contextual menu is owned by the parent via `onMoreClick`/`menuOpen`.
 */

import { Badge, type BadgeVariant, Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { WooProductType, type ShopProduct } from '../api/shop-types';
import { ImageOrDefault } from '../components/ImageOrDefault';
import { STOCK_LABELS, productPriceDisplay } from './product-list';
import styles from './ProductCard.module.css';

const STOCK_BADGE_VARIANT: Record<string, BadgeVariant> = {
  instock: 'success',
  outofstock: 'alert',
  onbackorder: 'warning',
  partialstock: 'warning',
};

interface ProductCardProps {
  product: ShopProduct;
  currencySymbol: string;
  menuOpen?: boolean;
  onMoreClick: (product: ShopProduct, triggerEl: HTMLButtonElement) => void;
  onCardClick: (product: ShopProduct) => void;
}

/**
 * Renders a product as a grid card.
 *
 * @param props - The product, currency symbol, menu state, and click handlers.
 *
 * @returns The card element.
 */
export function ProductCard({
  product,
  currencySymbol,
  menuOpen = false,
  onMoreClick,
  onCardClick,
}: ProductCardProps) {
  const intl = useIntl();
  const imageSrc = product.images[0]?.src;
  const isDraft = product.status === 'draft';
  // A variable product with divergent per-variant prices renders the honest
  // `min – max` range (no strikethrough); everything else keeps the sale-aware figure.
  const { label: price, regular: regularPrice } = productPriceDisplay(
    product,
    currencySymbol,
  );
  const isVariable = product.type === WooProductType.Variable;
  const variantCount = product.variant_count ?? 0;

  // Render the Draft badge AND the stock badge together (consistent across surfaces), rather
  // than the Draft badge replacing the stock badge.
  const stockBadge =
    product.stock_status in STOCK_BADGE_VARIANT
      ? {
          label: intl.formatMessage(STOCK_LABELS[product.stock_status]),
          variant: STOCK_BADGE_VARIANT[product.stock_status],
        }
      : null;

  return (
    <div
      className={`${styles.card} gv-p-md gv-gap-md gv-radius gv-border gv-shadow-subtle`}
      data-testid="product-card"
      data-id={product.id}
      onClick={() => onCardClick(product)}
    >
      <div className={`${styles.imageWrap} gv-border gv-radius`}>
        <ImageOrDefault src={imageSrc} alt={product.name} className={styles.image} />
        <div className={styles.badges}>
          {isDraft && (
            <Badge variant="generic">
              {intl.formatMessage(adminMessages.productsStatusDraft)}
            </Badge>
          )}
          {stockBadge && <Badge variant={stockBadge.variant}>{stockBadge.label}</Badge>}
        </div>
        <button
          className={`gv-button gv-mode-condensed gv-border gv-surface-bright gv-px-xs ${styles.moreButton}`}
          type="button"
          data-testid="product-card-actions"
          aria-label={intl.formatMessage(adminMessages.productsActions)}
          aria-expanded={menuOpen}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onMoreClick(product, e.currentTarget);
          }}
        >
          <Icon name="more_horiz" size="small" />
        </button>
      </div>
      <div className="gv-flex gv-flex-col">
        <span className={`gv-text-sm gv-text-bold ${styles.productName}`}>
          {product.name}
        </span>
        <div className="gv-flex gv-gap-xs gv-flex-wrap gv-items-baseline">
          {price && <span className="gv-text-sm">{price}</span>}
          {regularPrice && (
            <span className={`gv-text-sm gv-text-muted ${styles.regularPrice}`}>
              {regularPrice}
            </span>
          )}
        </div>
        {isVariable && (
          <span className="gv-caption-sm gv-text-muted" data-testid="product-card-variants">
            <FormattedMessage
              {...adminMessages.productsCardVariants}
              values={{ count: variantCount }}
            />
          </span>
        )}
      </div>
    </div>
  );
}
